import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  ChevronLeft, 
  Plus, 
  Trash2, 
  Loader2, 
  AlertTriangle, 
  CheckCircle,
  FileText,
  Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';

interface Account {
  id: number;
  code: string;
  name: string;
  type: string;
  isActive: boolean;
}

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc: string;
  isDefault: boolean;
}

const lineSchema = z.object({
  accountId: z.number().min(1, 'Seleccione una cuenta'),
  debit: z.string().default('0'),
  credit: z.string().default('0'),
  memo: z.string().optional(),
});

const journalEntrySchema = z.object({
  date: z.string().min(1, 'La fecha es requerida'),
  description: z.string().min(1, 'La descripción es requerida').max(500),
  reference: z.string().max(100).optional(),
  empresaId: z.number().nullable().optional(),
  lines: z.array(lineSchema).min(2, 'Se requieren al menos 2 líneas'),
});

type JournalEntryForm = z.infer<typeof journalEntrySchema>;

async function fetchAccounts(): Promise<Account[]> {
  const response = await fetch('/api/finance/accounts', {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Error al cargar cuentas');
  return response.json();
}

async function createJournalEntry(data: JournalEntryForm) {
  const response = await fetch('/api/finance/transactions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      date: data.date,
      description: data.description,
      reference: data.reference || undefined,
      empresaId: data.empresaId || undefined,
      lines: data.lines.map(line => ({
        accountId: line.accountId,
        debit: line.debit || '0',
        credit: line.credit || '0',
        memo: line.memo || undefined,
      })),
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || error.error || 'Error al crear la póliza');
  }
  return response.json();
}

function TotalsFooter({ control }: { control: any }) {
  const lines = useWatch({ control, name: 'lines' });
  
  const { totalDebit, totalCredit, difference, isBalanced } = useMemo(() => {
    let debitSum = 0;
    let creditSum = 0;
    
    (lines || []).forEach((line: any) => {
      debitSum += parseFloat(line.debit) || 0;
      creditSum += parseFloat(line.credit) || 0;
    });
    
    const diff = Math.abs(debitSum - creditSum);
    return {
      totalDebit: debitSum,
      totalCredit: creditSum,
      difference: diff,
      isBalanced: diff < 0.01,
    };
  }, [lines]);

  return (
    <div className="border-t bg-gray-50 p-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-xs text-gray-500 uppercase">Total Debe</p>
            <p className="text-lg font-bold text-blue-600">
              ${totalDebit.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 uppercase">Total Haber</p>
            <p className="text-lg font-bold text-green-600">
              ${totalCredit.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500 uppercase">Diferencia</p>
            <p className={`text-lg font-bold ${isBalanced ? 'text-gray-400' : 'text-red-600'}`}>
              ${difference.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        
        {!isBalanced && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-medium">El asiento está descuadrado</span>
          </div>
        )}
        
        {isBalanced && totalDebit > 0 && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm font-medium">Asiento cuadrado</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewJournalEntry() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [accountSearch, setAccountSearch] = useState<Record<number, string>>({});

  const { data: accounts = [], isLoading: loadingAccounts } = useQuery({
    queryKey: ['finance-accounts'],
    queryFn: fetchAccounts,
  });

  const { data: empresas = [] } = useQuery<Empresa[]>({
    queryKey: ['finance-emisores'],
    queryFn: async () => {
      const res = await fetch('/api/finance/emisores', { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const form = useForm<JournalEntryForm>({
    resolver: zodResolver(journalEntrySchema),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      description: '',
      reference: '',
      empresaId: null,
      lines: [
        { accountId: 0, debit: '', credit: '', memo: '' },
        { accountId: 0, debit: '', credit: '', memo: '' },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'lines',
  });

  const watchedLines = useWatch({ control: form.control, name: 'lines' });

  const { totalDebit, totalCredit, isBalanced } = useMemo(() => {
    let debitSum = 0;
    let creditSum = 0;
    
    (watchedLines || []).forEach((line) => {
      debitSum += parseFloat(line.debit) || 0;
      creditSum += parseFloat(line.credit) || 0;
    });
    
    return {
      totalDebit: debitSum,
      totalCredit: creditSum,
      isBalanced: Math.abs(debitSum - creditSum) < 0.01,
    };
  }, [watchedLines]);

  const createMutation = useMutation({
    mutationFn: createJournalEntry,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['finance-transactions'] });
      await queryClient.refetchQueries({ queryKey: ['finance-transactions'] });
      toast({
        title: 'Póliza creada',
        description: `La póliza #${data.transactionId} se ha creado exitosamente.`,
      });
      navigate('/app/finance/transactions');
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: JournalEntryForm) => {
    const validLines = data.lines.filter(
      line => line.accountId > 0 && (parseFloat(line.debit) > 0 || parseFloat(line.credit) > 0)
    );
    
    if (validLines.length < 2) {
      toast({
        title: 'Error',
        description: 'Se requieren al menos 2 líneas con montos válidos',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate({
      ...data,
      lines: validLines,
    });
  };

  const handleAddLine = () => {
    append({ accountId: 0, debit: '', credit: '', memo: '' });
  };

  const getFilteredAccounts = (index: number) => {
    const search = (accountSearch[index] || '').toLowerCase();
    if (!search) return accounts.filter(a => a.isActive);
    return accounts.filter(
      a => a.isActive && (
        a.code.toLowerCase().includes(search) ||
        a.name.toLowerCase().includes(search)
      )
    );
  };

  const getAccountDisplay = (accountId: number) => {
    const account = accounts.find(a => a.id === accountId);
    return account ? `${account.code} - ${account.name}` : 'Seleccionar cuenta';
  };

  const canSubmit = isBalanced && totalDebit > 0 && !createMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/app/finance/chart-of-accounts')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-blue-600" />
              Nueva Póliza Manual
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Crea un asiento contable con débitos y créditos cuadrados
            </p>
          </div>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          {/* Header Fields */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Datos de la Póliza</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Fecha *</Label>
                <Input
                  id="date"
                  type="date"
                  {...form.register('date')}
                  className={form.formState.errors.date ? 'border-red-500' : ''}
                />
                {form.formState.errors.date && (
                  <p className="text-xs text-red-500">{form.formState.errors.date.message}</p>
                )}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Descripción *</Label>
                <Input
                  id="description"
                  placeholder="Ej: Pago de nómina quincenal"
                  {...form.register('description')}
                  className={form.formState.errors.description ? 'border-red-500' : ''}
                />
                {form.formState.errors.description && (
                  <p className="text-xs text-red-500">{form.formState.errors.description.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reference">Referencia (opcional)</Label>
                <Input
                  id="reference"
                  placeholder="Ej: FAC-001234"
                  {...form.register('reference')}
                />
              </div>

              {empresas.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="empresaId">Empresa (opcional)</Label>
                  <Controller
                    name="empresaId"
                    control={form.control}
                    render={({ field }) => (
                      <Select
                        value={field.value?.toString() ?? 'none'}
                        onValueChange={(v) => field.onChange(v === 'none' ? null : parseInt(v))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sin empresa (global al tenant)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin empresa (global al tenant)</SelectItem>
                          {empresas.map((emp) => (
                            <SelectItem key={emp.id} value={emp.id.toString()}>
                              {emp.legalName}{emp.rfc ? ` (${emp.rfc})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <p className="text-xs text-gray-500">Asocia esta póliza al libro contable de una empresa específica.</p>
                </div>
              )}
            </div>
          </div>

          {/* Lines Table */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Líneas del Asiento</h2>
              <Button type="button" variant="outline" size="sm" onClick={handleAddLine}>
                <Plus className="w-4 h-4 mr-1" />
                Agregar Línea
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium w-[40%]">Cuenta</th>
                    <th className="text-right py-3 px-4 font-medium w-[20%]">Débito</th>
                    <th className="text-right py-3 px-4 font-medium w-[20%]">Crédito</th>
                    <th className="text-left py-3 px-4 font-medium w-[15%]">Memo</th>
                    <th className="text-center py-3 px-4 font-medium w-[5%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => (
                    <tr key={field.id} className="border-t hover:bg-gray-50">
                      <td className="py-2 px-4">
                        <Controller
                          control={form.control}
                          name={`lines.${index}.accountId`}
                          render={({ field: controllerField }) => (
                            <Select
                              value={controllerField.value?.toString() || ''}
                              onValueChange={(value) => controllerField.onChange(parseInt(value))}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Seleccionar cuenta">
                                  {controllerField.value ? getAccountDisplay(controllerField.value) : 'Seleccionar cuenta'}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <div className="p-2 sticky top-0 bg-white">
                                  <div className="relative">
                                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                                    <Input
                                      placeholder="Buscar cuenta..."
                                      className="pl-8"
                                      value={accountSearch[index] || ''}
                                      onChange={(e) => setAccountSearch(prev => ({ ...prev, [index]: e.target.value }))}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                </div>
                                {loadingAccounts ? (
                                  <div className="p-4 text-center text-gray-500">
                                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                                  </div>
                                ) : (
                                  getFilteredAccounts(index).map((account) => (
                                    <SelectItem key={account.id} value={account.id.toString()}>
                                      <span className="font-mono text-xs mr-2">{account.code}</span>
                                      {account.name}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="text-right font-mono"
                          {...form.register(`lines.${index}.debit`)}
                        />
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="text-right font-mono"
                          {...form.register(`lines.${index}.credit`)}
                        />
                      </td>
                      <td className="py-2 px-4">
                        <Input
                          placeholder="Nota"
                          className="text-sm"
                          {...form.register(`lines.${index}.memo`)}
                        />
                      </td>
                      <td className="py-2 px-4 text-center">
                        {fields.length > 2 && (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar línea"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals Footer */}
            <TotalsFooter control={form.control} />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/app/finance/chart-of-accounts')}
              disabled={createMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="min-w-[140px]"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar Póliza'
              )}
            </Button>
          </div>

          {!isBalanced && totalDebit > 0 && (
            <p className="text-sm text-red-500 text-right mt-2">
              El botón "Guardar" se habilitará cuando el asiento esté cuadrado
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
